import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Grid,
  TextField,
  Tooltip,
  DialogContentText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { postEmailIgnoreForm } from '../../../../api/vendors';
import { useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

IgnoreModal.propTypes = {
  fetchData: PropTypes.func,
  edit: PropTypes.bool,
  row: PropTypes.object,
  ignoreAreasValues: PropTypes.array,
};

export default function IgnoreModal({ fetchData, edit, row, ignoreAreasValues }) {
  const {
    data: { vendors },
  } = useSelector((state) => state.vendors);
  const [open, setOpen] = useState(false);
  const [ignoreAreas, setIgnoreAreas] = useState([]);

  useEffect(() => {
    if (ignoreAreasValues) {
      const keyPairType = ignoreAreasValues.map((row) => ({
        id: row.id,
        label: row.name,
      }));
      setIgnoreAreas(keyPairType);
    }
  }, [ignoreAreasValues]);

  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState({
    vendor: '',
    area: '',
    value: '',
    allSites: false,
  });

  const handleSave = async () => {
    const response = await postEmailIgnoreForm(value, edit ? row?.id : null);
    if (response) {
      enqueueSnackbar('Email pickup ignore added successfully', { variant: 'success' });
      setOpen(false);
    } else {
      enqueueSnackbar('Failed to add pickup ignore pickup', { variant: 'error' });
    }
    fetchData();
  };

  const handleClickOpen = () => {
    setValue({
      vendor: edit ? { id: row.vendorId, label: vendors.find((vendor) => vendor.id === row.vendorId).name } : '',
      area: edit ? { id: row.areaId, label: ignoreAreasValues.find((area) => area.id === row.areaId).name } : '',
      value: edit ? row.value : '',
      allSites: edit ? row.allSites : false,
    });
    setOpen(true);
  };
  const handleClose = () => {
    setValue({
      vendor: '',
      value: '',
      area: '',
      allSites: false,
    });
    setOpen(false);
  };

  return (
    <>
      {edit ? (
        <Tooltip title="Edit email pickup ignore">
          <IconButton edge="end" color="secondary" onClick={handleClickOpen} sx={{ mr: 0.5 }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="contained"
          sx={{ ml: 1, mr: 1 }}
          size="small"
          color="secondary"
          onClick={handleClickOpen}
          startIcon={<Iconify icon="eva:plus-outline" />}
        >
          Add Email Pickup Ignore
        </Button>
      )}

      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          {edit ? 'Edit' : 'Add'} Email Pickup Ignore
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>
        <DialogContent>
          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Vendor</DialogContentText>
              <Autocomplete
                size="small"
                fullWidth
                value={value.vendor}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    vendor: newValue,
                  });
                }}
                options={vendors?.map((type) => ({ id: type.id, label: type.name }))}
                // isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
              />
            </Grid>
          </Grid>
          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Area</DialogContentText>
              <Autocomplete
                size="small"
                fullWidth
                value={value.area}
                onChange={(event, newValue) => {
                  setValue({
                    ...value,
                    area: newValue,
                  });
                }}
                options={ignoreAreas}
                renderInput={(params) => <TextField {...params} label="Search or select" autoComplete="off"/>}
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
              />
            </Grid>
          </Grid>

          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Search Value</DialogContentText>
              <TextField
                fullWidth
                size="small"
                value={value.value}
                onChange={(e) =>
                  setValue({
                    ...value,
                    value: e.target.value,
                  })
                }
                label=""
                sx={{
                  mt: 0.5,
                  '& legend': { display: 'none' },
                  '& fieldset': { top: 0 },
                  '& .MuiFormLabel-root ': { display: 'none' },
                }}
                autoComplete="off"
              />
            </Grid>
          </Grid>
          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.allSites}
                    onChange={(event) => {
                      setValue({
                        ...value,
                        allSites: event.target.checked,
                      });
                    }}
                  />
                }
                label="All Sites"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton variant="contained" color="primary" size="medium" sx={{ width: 150 }} onClick={handleSave}>
            {edit ? 'Save' : 'Add Ignore'}
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
